import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { AppDispatch } from "state/store"
import { Supplier } from "types"
import useSuppliers from "hooks/useSuppliers"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { logger } from "utils/secureLogger"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import Section from "components/layout/section"
import ProcessingButton from "components/inputs/processingButton"
import Form from "components/forms/form"
import FormErrors from "components/formParts/formErrors"
import TextInput from "components/inputs/textInput"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"

interface DeleteSupplierFormProps {
    supplier: Supplier
    storageId: string
    supplierId: string
    onCancel: () => void
    onSuccess?: () => void
}

const DeleteSupplierForm: React.FC<DeleteSupplierFormProps> = ({
    supplier,
    storageId,
    supplierId,
    onCancel,
    onSuccess
}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch<AppDispatch>()
    const { deleteSupplierAsync, isDeleting } = useSuppliers({ storageId })
    
    const [confirmation, setConfirmation] = useState('')
    const [confirmed, setConfirmed] = useState(false)
    const [errors, setErrors] = useState<string[]>([])

    // Handle confirmation change.
    const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmation(e.target.value)
        setConfirmed(e.target.value === supplier.name)
    }

    const handleSubmit = async (event: React.FormEvent) => {
        logger.debug('Delete supplier form submitted', undefined, 'DeleteSupplier')
        setErrors([])
        event.preventDefault()
        
        if (!confirmed) return
        
        try {
            await deleteSupplierAsync({ storageId, supplierId })
            logger.info('Supplier deleted successfully', { storageId, supplierId, supplierName: supplier.name })
            if (onSuccess) onSuccess()
        } catch (e: unknown) {
            const errorMessage = decodeFirebaseError(e as string)
            setErrors([errorMessage])
            logger.error('Supplier deletion failed', { error: e, storageId, supplierId })
        }
    }

    return (
        <Form onSubmit={handleSubmit} isLoading={isDeleting}>
            <Section>
                <p>{t('confirmDeleteSupplier', { supplierName: supplier.name })}</p>
                <TextInput 
                    label={t('typeSupplierNameToConfirm')} 
                    value={confirmation} 
                    onChange={handleConfirmationChange} 
                    placeholder={supplier.name} 
                />
                
                {errors.length > 0 && <FormErrors errors={errors} />}
                
                <ButtonGroup>
                    <Button wideOnMobile
                        design="optional"
                        onClick={onCancel}
                        disabled={isDeleting}
                        type="button"
                    >
                        <CloseIcon />
                        <span>{t('buttonCancel')}</span>
                    </Button>
                    
                    {isDeleting ? (
                        <ProcessingButton design="danger" />
                    ) : (
                        <Button wideOnMobile type="submit" design="danger" disabled={!confirmed}>
                            <DeleteIcon />
                            <span>{t('buttonDelete')}</span>
                        </Button>
                    )}
                </ButtonGroup>
            </Section>
        </Form>
    )
}

export default DeleteSupplierForm
